//#region src/surfaces/surface-rule-ids.ts
/**
* The rule ids the six surface publish rules emit (layered shell surfaces, D20, R-20, R-35; surface
* extension points).
*
* One spelling for every reader: the CP publish door (WS-4) prints `<rule>: <message>` in its
* HTTP 400, `zion-app validate` (WS-14) prints the same line, and tests compare against these
* lists. An id is a gate name. It is never renamed for prose; a reworded message keeps its id.
*
* Each check module emits ids from its own group only, so a reader can tell which rule refused a
* bundle from the id alone. Every id matches `^[a-z][a-z0-9-]*$`.
*/
/** The ids `checkSurfaceDeclarations` emits: the cross-field rules JSON Schema cannot state. */
const SURFACE_DECLARATION_RULE_IDS = Object.freeze([
	"surface-name-duplicate",
	"surface-root-multiple",
	"surface-root-not-routed",
	"surface-root-has-input",
	"surface-root-has-outputs",
	"surface-root-has-mount-path",
	"surface-routed-mount-path-required",
	"surface-routed-default-route-required",
	"surface-embedded-route-forbidden",
	"surface-mount-path-duplicate",
	"surface-uses-duplicate",
	"surface-settings-on-root-only"
]);
/** The ids `checkSurfaceTypeLanguage` emits: a declared schema outside the closed type language (D8). */
const SURFACE_TYPE_LANGUAGE_RULE_IDS = Object.freeze([
	"surface-schema-not-representable",
	"surface-schema-too-deep",
	"surface-schema-too-large"
]);
/** The ids `checkSharedModules` emits: a `sharedDependencies` value the import map cannot serve (D12). */
const SHARED_MODULE_RULE_IDS = Object.freeze([
	"shared-module-grammar",
	"shared-module-not-served",
	"shared-module-duplicate"
]);
/** The id `checkRootEligibility` emits: a root surface from an app outside `ROOT_SHELL_APPS` (D2, D20). */
const ROOT_ELIGIBILITY_RULE_IDS = Object.freeze(["surface-root-app-not-eligible"]);
/** The ids `checkSurfaceUses` emits: a `uses[]` row that does not hold against its counterparts (D21, R-6). */
const SURFACE_USES_RULE_IDS = Object.freeze([
	"surface-uses-app-unknown",
	"surface-uses-surface-unknown",
	"surface-uses-not-embedded",
	"surface-uses-input-unsatisfied",
	"surface-uses-output-unsatisfied",
	"surface-uses-self",
	"surface-uses-cycle",
	"surface-breaks-pinned-consumer"
]);
/**
* The ids `checkExtensionPoints` emits: an `extensionPoints[]` row, a point list on a surface row, or
* a `uses[].point` row that does not hold (surface extension points, D2, D5, D7, D8, D9).
*
* The first seven are pure: they read the manifest alone, and `zion-app validate` emits them. The
* last seven need the registry view (`ExtensionPointContext.points` and `ownPublished`); a caller
* that passes `null` for both gets none of them for a foreign id. Every schema check reuses
* `findAssignabilityMismatch(writer, reader)`. For an implementer the point is the writer of the
* input and the reader of the outputs; for a `uses[].point` row the row is the writer of the input
* and the point is the writer of the outputs.
*
* - `extension-point-id-invalid`: an id fails `EXTENSION_POINT_ID_PATTERN` or its length cap.
* - `extension-point-owner-mismatch`: a declared id's prefix is not the publishing slug.
* - `extension-point-duplicate`: two rows declare one id, or one list names an id twice.
* - `extension-point-fill-invalid`: a slot has no `fill`, or a surface point has one.
* - `surface-implements-not-embedded`: `implements` on a routed or root row.
* - `surface-slots-not-root`: `slots` on a non-root row.
* - `surface-fills-on-root`: `fills` on a root row.
* - `extension-point-unknown`: a reference names no published point.
* - `extension-point-kind-mismatch`: `implements` names an item slot; `fills` or `slots` names a
*   surface point; `uses[].point` names a slot.
* - `extension-point-changed`: a row redeclares a published id with another schema.
* - `surface-implements-input-unsatisfied`: the point's input does not fit the implementer's input.
* - `surface-implements-output-unsatisfied`: an implementer lacks a point output, or its output does
*   not fit.
* - `surface-uses-point-input-unsatisfied`: a row's inputs do not fit the point's input.
* - `surface-uses-point-output-unsatisfied`: a point output does not fit what the row reads.
*
* The door-only id `extension-points-disabled` (D14) is never in this list: the CP publish door
* emits it from its `EXTENSION_POINTS_MODE` setting, and the CLI never does.
*/
const EXTENSION_POINT_RULE_IDS = Object.freeze([
	"extension-point-id-invalid",
	"extension-point-owner-mismatch",
	"extension-point-duplicate",
	"extension-point-fill-invalid",
	"surface-implements-not-embedded",
	"surface-slots-not-root",
	"surface-fills-on-root",
	"extension-point-unknown",
	"extension-point-kind-mismatch",
	"extension-point-changed",
	"surface-implements-input-unsatisfied",
	"surface-implements-output-unsatisfied",
	"surface-uses-point-input-unsatisfied",
	"surface-uses-point-output-unsatisfied"
]);
/**
* Every surface rule id, the six groups in door order (R-20 Q14 lists the binding order of the first
* five; the extension point group is appended last). Frozen.
*/
const SURFACE_RULE_IDS = Object.freeze([
	...SURFACE_DECLARATION_RULE_IDS,
	...SURFACE_TYPE_LANGUAGE_RULE_IDS,
	...SHARED_MODULE_RULE_IDS,
	...ROOT_ELIGIBILITY_RULE_IDS,
	...SURFACE_USES_RULE_IDS,
	...EXTENSION_POINT_RULE_IDS
]);
//#endregion
export { EXTENSION_POINT_RULE_IDS, ROOT_ELIGIBILITY_RULE_IDS, SHARED_MODULE_RULE_IDS, SURFACE_DECLARATION_RULE_IDS, SURFACE_RULE_IDS, SURFACE_TYPE_LANGUAGE_RULE_IDS, SURFACE_USES_RULE_IDS };

//# sourceMappingURL=surface-rule-ids.js.map